import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

/* ── noise texture (inline SVG) ─────────────────────────────────────────── */
const noiseUrl =
  "data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E";

const grain = {
  content: '""',
  position: 'absolute',
  inset: 0,
  backgroundImage: `url("${noiseUrl}")`,
  opacity: 0.045,
  mixBlendMode: 'overlay',
  pointerEvents: 'none',
  zIndex: 2,
};

/* ── Magnetic button wrapper ────────────────────────────────────────────── */
const MagneticWrap = ({ children, strength = 0.3 }) => {
  const ref = useRef(null);
  const posRef = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [hovering, setHovering] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!hovering) return;
    const handleMove = (e) => {
      if (!ref.current) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const rect = ref.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const nx = (e.clientX - cx) * strength;
        const ny = (e.clientY - cy) * strength;
        posRef.current = { x: nx, y: ny };
        setPos({ x: nx, y: ny });
      });
    };
    document.addEventListener('mousemove', handleMove);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [hovering, strength]);

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => { setHovering(false); setPos({ x: 0, y: 0 }); }}
      style={{
        display: 'inline-block',
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        transition: hovering
          ? 'transform 0.12s ease-out'
          : 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {children}
    </div>
  );
};

/* ── data ────────────────────────────────────────────────────────────────── */
const services = [
  {
    id: '01',
    title: 'RESTAURATION',
    description: 'Commandez vos plats préférés depuis les meilleurs restaurants de Mayotte',
    image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&h=600&fit=crop',
    path: '/restaurants',
    radius: '20px',
  },
  {
    id: '02',
    title: 'COVOITURAGE',
    description: 'Partagez vos trajets quotidiens et économisez ensemble',
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&h=600&fit=crop',
    path: '/carpool',
    radius: '14px',
    soon: true,
  },
  {
    id: '03',
    title: 'LOCATION AUTO',
    description: 'Louez un véhicule adapté à tous vos besoins',
    image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800&h=600&fit=crop',
    path: '/car-rental',
    radius: '16px',
    soon: true,
  },
];

/* ── Hero carousel slides ───────────────────────────────────────────────── */
const carouselSlides = [
  {
    id: 'ladas',
    leftBg: '#552583',
    rightBg: '#1a1a1a',
    leftContent: (
      <>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', mb: 1.5 }}>
          usafiri · nouveau partenaire
        </Typography>
        <Typography sx={{ fontSize: { xs: '0.85rem', md: '1rem' }, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6, mb: 1.5 }}>
          Notre premier<br />partenaire restaurateur
        </Typography>
        <Box sx={{ width: 28, height: 2, bgcolor: '#FDB927' }} />
      </>
    ),
    rightContent: (
      <>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', textTransform: 'uppercase', mb: 2 }}>
          nouveau
        </Typography>
        <Typography sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 900, lineHeight: 1, color: 'white', letterSpacing: '-0.025em' }}>
          LADA'S<br />PIZZA
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: '0.7rem', fontWeight: 700, color: '#FDB927', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Maintenant sur Usafiri
        </Typography>
      </>
    ),
    barBg: '#FDB927',
    barText: 'Commander maintenant',
    barTextColor: '#33154f',
    link: '/restaurants/ladas-pizza',
  },
  {
    id: 'launch',
    fullBg: '#552583',
    fullContent: (
      <>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', mb: 2 }}>
          usafiri · livraison à mayotte
        </Typography>
        <Typography sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.03em', color: 'white', mb: 1.5 }}>
          La livraison<br />arrive à<br /><Box component="span" sx={{ color: '#FDB927' }}>Mayotte.</Box>
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, mb: 3 }}>
          Commande tes repas en ligne.<br />Livré chez toi en moins de 45 min.
        </Typography>
        <Box sx={{ display: 'inline-block', bgcolor: '#FDB927', color: '#33154f', fontSize: '0.75rem', fontWeight: 800, px: 2.5, py: 1, borderRadius: '20px' }}>
          Voir les restaurants
        </Box>
      </>
    ),
    link: '/restaurants',
  },
  {
    id: 'livreur',
    leftBg: '#f5f0eb',
    rightBg: '#552583',
    leftDark: false,
    leftContent: (
      <>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.12em', color: '#aaa', textTransform: 'uppercase', mb: 1.5 }}>
          usafiri recrute
        </Typography>
        <Typography sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.03em', color: '#552583', mb: 1.5 }}>
          DEVIENS<br />LIVREUR.
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: '#666', lineHeight: 1.7 }}>
          Scooter ou voiture.<br />Statut auto-entrepreneur.<br />On t'accompagne.
        </Typography>
      </>
    ),
    rightContent: (
      <>
        <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: '12px', p: 2, textAlign: 'center', width: '100%', mb: 1.5 }}>
          <Typography sx={{ fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 900, color: '#FDB927', lineHeight: 1 }}>30</Typography>
          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'white', letterSpacing: '0.08em', textTransform: 'uppercase' }}>jours</Typography>
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', mt: 0.5, lineHeight: 1.5 }}>pour envoyer<br />ton KBIS</Typography>
        </Box>
        <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.5 }}>
          Commence à livrer<br />dès maintenant
        </Typography>
      </>
    ),
    barBg: '#1a1a1a',
    barText: 'Je veux livrer avec Usafiri',
    barTextColor: 'white',
    barAccent: '#FDB927',
    link: '/partner/register?type=livreur',
  },
  {
    id: 'howto',
    fullBg: '#1a1a1a',
    fullContent: (
      <>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.25)', textTransform: 'uppercase', mb: 2 }}>
          comment ça marche
        </Typography>
        <Typography sx={{ fontSize: { xs: '1.8rem', md: '2.4rem' }, fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.025em', color: 'white', mb: 3 }}>
          3 étapes.<br /><Box component="span" sx={{ color: '#552583' }}>C'est tout.</Box>
        </Typography>
        {[
          { n: '1', label: 'Choisis ton restaurant', accent: false },
          { n: '2', label: 'Compose ta commande', accent: false },
          { n: '3', label: <>Reçois chez toi. <Box component="span" sx={{ color: '#FDB927' }}>45 min max.</Box></>, accent: true },
        ].map(({ n, label, accent }) => (
          <Box key={n} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: accent ? '#FDB927' : '#552583', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 900, color: accent ? '#33154f' : 'white' }}>{n}</Typography>
            </Box>
            <Typography sx={{ fontSize: { xs: '0.85rem', md: '0.95rem' }, color: accent ? 'white' : 'rgba(255,255,255,0.7)', fontWeight: accent ? 700 : 400 }}>{label}</Typography>
          </Box>
        ))}
      </>
    ),
    barBg: '#552583',
    barText: 'Commander maintenant',
    barTextColor: 'white',
    link: '/restaurants',
  },
];

/* ── HeroCarousel component ─────────────────────────────────────────────── */
const HeroCarousel = () => {
  const [cur, setCur] = useState(0);
  const [animDir, setAnimDir] = useState(null);
  const autoRef = useRef(null);
  const n = carouselSlides.length;

  const goTo = useCallback((idx, dir) => {
    setAnimDir(dir);
    setCur((idx + n) % n);
  }, [n]);

  const next = useCallback(() => goTo(cur + 1, 'left'), [cur, goTo]);
  const prev = useCallback(() => goTo(cur - 1, 'right'), [cur, goTo]);

  useEffect(() => {
    autoRef.current = setInterval(next, 5000);
    return () => clearInterval(autoRef.current);
  }, [next]);

  const pause = () => clearInterval(autoRef.current);
  const resume = () => { autoRef.current = setInterval(next, 5000); };

  const slide = carouselSlides[cur];
  const navigate = useNavigate();

  return (
    <Box onMouseEnter={pause} onMouseLeave={resume}
      sx={{ position: 'relative', borderRadius: { xs: 0, md: '20px' }, overflow: 'hidden', mx: { xs: 0, md: 3 }, mb: { xs: 0, md: 4 } }}>

      {/* Slide body */}
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: 300, md: 340 }, cursor: 'pointer' }}
        onClick={() => navigate(slide.link)}>

        {slide.fullBg ? (
          /* full-width single-colour slide */
          <Box sx={{ flex: 1, bgcolor: slide.fullBg, p: { xs: 3, md: 5 }, display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', right: -40, top: -40, width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(253,185,39,0.06)' }} />
            <Box sx={{ position: 'absolute', right: 30, bottom: -50, width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(253,185,39,0.04)' }} />
            <Box sx={{ position: 'relative', zIndex: 1 }}>{slide.fullContent}</Box>
          </Box>
        ) : (
          /* split slide */
          <Box sx={{ flex: 1, display: 'flex' }}>
            <Box sx={{ flex: 1, bgcolor: slide.leftBg, p: { xs: 2.5, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              {slide.leftContent}
            </Box>
            <Box sx={{ flex: 1.2, bgcolor: slide.rightBg, p: { xs: 2.5, md: 4 }, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: slide.rightBg === '#552583' ? 'center' : 'flex-start' }}>
              {slide.rightContent}
            </Box>
          </Box>
        )}

        {/* Bottom bar */}
        {slide.barBg && (
          <Box sx={{ bgcolor: slide.barBg, px: { xs: 2.5, md: 4 }, py: 1.4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: { xs: '0.8rem', md: '0.88rem' }, fontWeight: 800, color: slide.barTextColor }}>
              {slide.barText}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 18, color: slide.barAccent || slide.barTextColor, opacity: 0.8 }} />
          </Box>
        )}
      </Box>

      {/* Prev / Next buttons */}
      <IconButton onClick={(e) => { e.stopPropagation(); prev(); }}
        sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.45)', color: 'white', width: 48, height: 48, '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
        <ChevronLeftIcon sx={{ fontSize: 32 }} />
      </IconButton>
      <IconButton onClick={(e) => { e.stopPropagation(); next(); }}
        sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(0,0,0,0.45)', color: 'white', width: 48, height: 48, '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}>
        <ChevronRightIcon sx={{ fontSize: 32 }} />
      </IconButton>

      {/* Dots */}
      <Box sx={{ position: 'absolute', bottom: slide.barBg ? 54 : 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: '6px', zIndex: 10 }}>
        {carouselSlides.map((_, i) => (
          <Box key={i} onClick={(e) => { e.stopPropagation(); goTo(i, i > cur ? 'left' : 'right'); }}
            sx={{ height: 4, borderRadius: 2, cursor: 'pointer', transition: 'all 0.25s', bgcolor: i === cur ? 'white' : 'rgba(255,255,255,0.35)', width: i === cur ? 20 : 6 }} />
        ))}
      </Box>
    </Box>
  );
};

const stats = [
  { value: '24/7', label: 'SERVICE DISPONIBLE' },
  { value: '100%', label: 'PARTENAIRES VÉRIFIÉS' },
  { value: '<30', label: 'MINUTES LIVRAISON' },
  { value: '4.9', label: 'NOTE MOYENNE' },
];

/* ── hero media ─────────────────────────────────────────────────────────── */
const HERO_VIDEO =
  'https://videos.pexels.com/video-files/1409899/1409899-hd_1920_1080_25fps.mp4';
const HERO_POSTER =
  'https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=1920&h=1080&fit=crop&q=80';

/* ── Component ──────────────────────────────────────────────────────────── */
const Home = () => {
  const navigate = useNavigate();
  const servicesRef = useRef(null);
  const [servicesVisible, setServicesVisible] = useState(false);

  /* tilt cards */
  const [tilt, setTilt] = useState({ idx: -1, rx: 0, ry: 0 });

  const observerCallback = useCallback((entries) => {
    if (entries[0].isIntersecting) setServicesVisible(true);
  }, []);

  useEffect(() => {
    const el = servicesRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(observerCallback, { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [observerCallback]);

  const handleServiceClick = (service) => {
    if (service.soon) return;
    navigate(service.path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToServices = () => {
    const el = servicesRef.current;
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 40;
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  /* tilt handlers */
  const handleTiltMove = useCallback((e, idx) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ idx, rx: y * -8, ry: x * 8 });
  }, []);

  const handleTiltLeave = useCallback(() => {
    setTilt({ idx: -1, rx: 0, ry: 0 });
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* mesh gradient keyframes */}
      <style>{`
        @keyframes meshBlob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(25%, 15%) scale(1.15); }
          66% { transform: translate(-8%, 25%) scale(0.9); }
        }
        @keyframes meshBlob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-18%, -10%) scale(1.1); }
          66% { transform: translate(14%, -22%) scale(0.95); }
        }
        @keyframes meshBlob3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(10%, 14%) scale(1.08); }
        }
      `}</style>

      {/* ═══════════════ HERO — video background ═══════════════ */}
      <Box
        sx={{
          position: 'relative',
          height: '95vh',
          overflow: 'hidden',
        }}
      >
        {/* Video background */}
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          poster={HERO_POSTER}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </Box>

        {/* Duotone overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(160deg, rgba(26,10,46,0.92) 0%, rgba(85,37,131,0.78) 40%, rgba(26,10,46,0.88) 100%)',
            zIndex: 1,
          }}
        />

        {/* Grain texture */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("${noiseUrl}")`,
            opacity: 0.045,
            mixBlendMode: 'overlay',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        />

        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 3,
          }}
        >
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 2.5,
              fontSize: { xs: '2.8rem', md: '5.5rem', lg: '7rem' },
              lineHeight: 0.95,
              fontWeight: 800,
              letterSpacing: '-0.02em',
            }}
          >
            USAFIRI
          </Typography>

          <Typography
            sx={{
              color: '#FDB927',
              mb: 5,
              fontWeight: 400,
              letterSpacing: '0.18em',
              fontSize: { xs: '0.85rem', md: '1.05rem' },
              textTransform: 'uppercase',
              opacity: 0.9,
            }}
          >
            La référence des services premium à Mayotte
          </Typography>

          <Box sx={{ maxWidth: { xs: '100%', md: '520px' } }}>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.55)',
                mb: 4,
                fontSize: { xs: '0.92rem', md: '1.05rem' },
                lineHeight: 1.8,
                fontWeight: 400,
              }}
            >
              Restauration, covoiturage et location de véhicules
              réunis en une seule plateforme.
            </Typography>

            <MagneticWrap strength={0.25}>
              <Button
                onClick={scrollToServices}
                sx={{
                  px: 4.5,
                  py: 1.8,
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  color: '#1a0a2e',
                  background: '#FDB927',
                  borderRadius: '10px',
                  border: 'none',
                  boxShadow: '0 4px 24px rgba(253,185,39,0.25)',
                  transition: 'background 0.25s ease-out, box-shadow 0.25s ease-out',
                  '&::before': { display: 'none' },
                  '&:hover': {
                    background: '#ffe066',
                    boxShadow: '0 8px 32px rgba(253,185,39,0.35)',
                  },
                }}
              >
                DÉCOUVRIR
                <ArrowForwardIcon
                  sx={{ ml: 1.2, fontSize: 18, transform: 'rotate(90deg)' }}
                />
              </Button>
            </MagneticWrap>
          </Box>

          {/* Geoloc label */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 40,
              right: 40,
              textAlign: 'right',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                letterSpacing: '0.25em',
                color: 'rgba(255,255,255,0.3)',
                fontSize: '0.65rem',
                fontWeight: 500,
              }}
            >
              MAYOTTE
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                letterSpacing: '0.25em',
                color: 'rgba(255,255,255,0.2)',
                fontSize: '0.65rem',
                fontWeight: 500,
              }}
            >
              OCÉAN INDIEN
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════ HERO CAROUSEL ═══════════════ */}
      <Box sx={{ pt: { xs: 0, md: 4 }, pb: { xs: 0, md: 0 } }}>
        <HeroCarousel />
      </Box>

      {/* ═══════════════ SERVICES — bento grid with 3D tilt ═══════════════ */}
      <Box
        ref={servicesRef}
        sx={{ pt: 5, pb: 14, position: 'relative' }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h2"
            sx={{
              mb: { xs: 5, md: 7 },
              fontSize: { xs: '1.8rem', md: '2.8rem' },
              opacity: servicesVisible ? 1 : 0,
              transform: servicesVisible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          >
            NOS SERVICES
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' },
              gridTemplateRows: { xs: 'auto', md: '320px 220px' },
              gap: { xs: 2.5, md: '20px' },
            }}
          >
            {services.map((service, index) => (
              <Box
                key={service.id}
                onClick={() => handleServiceClick(service)}
                onMouseMove={(e) => handleTiltMove(e, index)}
                onMouseLeave={handleTiltLeave}
                sx={{
                  ...(index === 0 && { gridRow: { md: '1 / 3' } }),
                  cursor: service.soon ? 'default' : 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  height: { xs: '260px', sm: '300px', md: 'auto' },
                  minHeight: { md: index === 0 ? 0 : index === 1 ? '320px' : '220px' },
                  borderRadius: service.radius,
                  boxShadow:
                    '0 1px 2px rgba(0,0,0,0.06), 0 12px 40px -8px rgba(85,37,131,0.12)',
                  transformStyle: 'preserve-3d',
                  willChange: 'transform',
                  '&:hover': {
                    boxShadow:
                      '0 1px 3px rgba(0,0,0,0.08), 0 24px 64px -16px rgba(85,37,131,0.2)',
                    '& .card-img': { transform: 'scale(1.08)' },
                    '& .card-overlay': {
                      background:
                        index === 0
                          ? 'linear-gradient(180deg, rgba(26,10,46,0.3) 0%, rgba(26,10,46,0.75) 100%)'
                          : 'linear-gradient(160deg, rgba(26,10,46,0.5) 0%, rgba(85,37,131,0.3) 100%)',
                    },
                    '& .card-number': { color: '#FDB927', opacity: 1 },
                    '& .card-arrow': { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
                style={{
                  opacity: servicesVisible ? 1 : 0,
                  transform: !servicesVisible
                    ? `translateY(${40 + index * 15}px)`
                    : tilt.idx === index
                      ? `perspective(800px) rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`
                      : 'perspective(800px) rotateX(0deg) rotateY(0deg)',
                  transition: !servicesVisible
                    ? `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${index * 0.15}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${index * 0.15}s`
                    : tilt.idx === index
                      ? 'transform 0.1s ease-out'
                      : 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                {/* Image */}
                <Box
                  className="card-img"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${service.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                  }}
                />
                {/* Overlay */}
                <Box
                  className="card-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: index === 0
                      ? 'linear-gradient(180deg, rgba(26,10,46,0.45) 0%, rgba(26,10,46,0.82) 100%)'
                      : index === 1
                      ? 'linear-gradient(160deg, rgba(26,10,46,0.7) 0%, rgba(0,0,0,0.55) 100%)'
                      : 'linear-gradient(200deg, rgba(26,10,46,0.65) 0%, rgba(0,0,0,0.6) 100%)',
                    transition: 'background 0.4s ease',
                  }}
                />

                {/* Soon badge */}
                {service.soon && (
                  <Box sx={{
                    position: 'absolute', top: { xs: 12, md: 16 }, right: { xs: 12, md: 16 },
                    zIndex: 2, bgcolor: 'rgba(253,185,39,0.95)', color: '#552583',
                    px: 1.5, py: 0.5, borderRadius: '8px',
                    fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.05em',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                  }}>
                    BIENTÔT DISPONIBLE
                  </Box>
                )}

                {/* Content */}
                <Box
                  sx={{
                    position: 'relative',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    p: { xs: 2.5, md: index === 0 ? 4 : 2.5 },
                    /* slight Z-lift for depth on tilt */
                    transform: 'translateZ(20px)',
                    transformStyle: 'preserve-3d',
                  }}
                >
                  {/* Number */}
                  <Typography
                    className="card-number"
                    sx={{
                      color: 'rgba(255,255,255,0.15)',
                      fontFamily: '"JetBrains Mono", "IBM Plex Mono", monospace',
                      fontWeight: 300,
                      lineHeight: 1,
                      transition: 'all 0.3s ease',
                      ...(index === 0
                        ? { fontSize: '5rem', mb: 2, position: 'absolute', top: 28, left: 32 }
                        : { fontSize: '1.6rem', mb: 1 }),
                    }}
                  >
                    {service.id}
                  </Typography>

                  {/* Title + arrow */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.8 }}>
                    <Typography
                      sx={{
                        color: 'white',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        fontSize: index === 0 ? '1.4rem' : '0.95rem',
                        textTransform: 'uppercase',
                      }}
                    >
                      {service.title}
                    </Typography>
                    <ArrowForwardIcon
                      className="card-arrow"
                      sx={{
                        color: '#FDB927',
                        fontSize: index === 0 ? 22 : 16,
                        opacity: 0,
                        transform: 'translateX(-8px)',
                        transition: 'all 0.3s ease',
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      lineHeight: 1.5,
                      fontSize: index === 0 ? '0.88rem' : '0.78rem',
                      maxWidth: index === 0 ? '400px' : '100%',
                    }}
                  >
                    {service.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════ WHY USAFIRI — editorial + mesh gradient ═══════════════ */}
      <Box sx={{ position: 'relative' }}>

        {/* Mesh gradient blobs — slow organic movement */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: { xs: 280, md: 420 },
              height: { xs: 280, md: 420 },
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(85,37,131,0.07) 0%, transparent 70%)',
              filter: 'blur(60px)',
              top: '-5%',
              left: '8%',
              animation: 'meshBlob1 22s ease-in-out infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: { xs: 220, md: 340 },
              height: { xs: 220, md: 340 },
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(253,185,39,0.05) 0%, transparent 70%)',
              filter: 'blur(50px)',
              bottom: '5%',
              right: '12%',
              animation: 'meshBlob2 28s ease-in-out infinite',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: { xs: 180, md: 280 },
              height: { xs: 180, md: 280 },
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(85,37,131,0.04) 0%, transparent 70%)',
              filter: 'blur(45px)',
              top: '40%',
              right: '30%',
              animation: 'meshBlob3 18s ease-in-out infinite',
            }}
          />
        </Box>

        {/* Text block */}
        <Box sx={{ pt: { xs: 10, md: 14 }, pb: { xs: 4, md: 6 }, position: 'relative', zIndex: 1 }}>
          <Container maxWidth="xl">
            <Box sx={{ maxWidth: { md: '680px' } }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 3,
                  fontSize: { xs: '1.8rem', md: '2.6rem' },
                }}
              >
                POURQUOI USAFIRI ?
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(0,0,0,0.45)',
                  lineHeight: 2,
                  fontSize: { xs: '0.92rem', md: '1.02rem' },
                  fontWeight: 400,
                }}
              >
                Usafiri redéfinit l'expérience des services à Mayotte en combinant
                qualité, rapidité et fiabilité. Tous nos partenaires sont soigneusement
                sélectionnés pour vous garantir une expérience exceptionnelle.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Stats — full-width horizontal strip */}
        <Box
          sx={{
            borderTop: '0.5px solid rgba(85,37,131,0.1)',
            borderBottom: '0.5px solid rgba(85,37,131,0.1)',
            py: { xs: 5, md: 6 },
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="xl">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 4, sm: 0 },
              }}
            >
              {stats.map((stat, i) => {
                const sizes = ['3.4rem', '2.6rem', '2.8rem', '2.2rem'];
                const labelSizes = ['0.65rem', '0.6rem', '0.62rem', '0.58rem'];
                return (
                  <React.Fragment key={i}>
                    {i > 0 && (
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          width: '0.5px',
                          height: { sm: 48, md: 56 },
                          bgcolor: 'rgba(85,37,131,0.1)',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    {i > 0 && (
                      <Box
                        sx={{
                          display: { xs: 'block', sm: 'none' },
                          width: '32px',
                          height: '0.5px',
                          bgcolor: 'rgba(85,37,131,0.12)',
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        flex: 1,
                        textAlign: { xs: 'left', sm: 'center' },
                        px: { sm: 2 },
                        display: 'flex',
                        flexDirection: { xs: 'row', sm: 'column' },
                        alignItems: { xs: 'baseline', sm: 'center' },
                        gap: { xs: 2, sm: 0 },
                      }}
                    >
                      <Typography
                        sx={{
                          color: i === 0 ? '#552583' : 'rgba(85,37,131,0.75)',
                          fontSize: { xs: '2rem', md: sizes[i] },
                          fontWeight: i === 0 ? 800 : 700,
                          lineHeight: 1,
                          mb: { xs: 0, sm: 1 },
                          letterSpacing: '-0.02em',
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        sx={{
                          color: 'rgba(0,0,0,0.3)',
                          letterSpacing: '0.1em',
                          fontSize: { xs: '0.6rem', md: labelSizes[i] },
                          fontWeight: 500,
                          textTransform: 'uppercase',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>
          </Container>
        </Box>
      </Box>

      {/* ═══════════════ CTA ═══════════════ */}
      <Box
        sx={{
          py: { xs: 14, md: 18 },
          position: 'relative',
          backgroundImage:
            'url(https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=1920&h=800&fit=crop&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(160deg, rgba(10,6,18,0.93) 0%, rgba(85,37,131,0.82) 50%, rgba(10,6,18,0.9) 100%)',
          },
          '&::after': grain,
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3 }}>
          <Box textAlign="center">
            <Typography
              variant="h2"
              sx={{
                mb: 2.5,
                fontSize: { xs: '1.8rem', md: '3rem' },
                color: '#ffffff',
                fontWeight: 800,
              }}
            >
              VOUS ÊTES PROFESSIONNEL ?
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.45)',
                mb: 5,
                fontWeight: 400,
                letterSpacing: '0.1em',
                fontSize: { xs: '0.85rem', md: '1rem' },
              }}
            >
              REJOIGNEZ NOTRE RÉSEAU DE PARTENAIRES PREMIUM.
            </Typography>
            <MagneticWrap strength={0.3}>
              <Button
                component={Link}
                to="/partner/register"
                sx={{
                  px: 5.5,
                  py: 2,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  borderRadius: '10px',
                  color: '#FDB927',
                  border: '1.5px solid rgba(253,185,39,0.5)',
                  background: 'transparent',
                  boxShadow: 'none',
                  transition: 'background 0.25s ease-out, color 0.25s ease-out, border-color 0.25s ease-out, box-shadow 0.25s ease-out',
                  '&::before': { display: 'none' },
                  '&:hover': {
                    bgcolor: '#FDB927',
                    color: '#1a0a2e',
                    borderColor: '#FDB927',
                    boxShadow: '0 8px 32px rgba(253,185,39,0.25)',
                  },
                }}
              >
                DEVENIR PARTENAIRE
                <ArrowForwardIcon sx={{ ml: 1.2, fontSize: 18 }} />
              </Button>
            </MagneticWrap>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
